import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { showError, showSuccess } from "@/utils/toast";
import { ArrowLeft, ChevronLeft, ChevronRight, Copy, Trash2 } from "lucide-react";
import { AdminLayoutContext } from "@/contexts/AdminLayoutContext";

interface ImageItem {
    id: number;
    library_id?: number;
    image_url: string | null;
    description?: string;
    created_at: string;
    updated_at: string;
}

interface Rectangle {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

const GroupDicingPage: React.FC = () => {
    const { libraryId } = useParams<{ libraryId: string }>();
    const navigate = useNavigate();
    const { isSidebarOpen, toggleSidebar } = useContext(AdminLayoutContext);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const [images, setImages] = useState<ImageItem[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [libraryName] = useState("Könyvtár");

    const [rectangles, setRectangles] = useState<Rectangle[]>([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
    const [scale, setScale] = useState(1);
    const [slices, setSlices] = useState<any[]>([]);
    const [slicesLoading, setSlicesLoading] = useState(false);
    const [diceLoading, setDiceLoading] = useState(false);
    const [selectedArea, setSelectedArea] = useState<Rectangle | null>(null);
    const [previewImage, setPreviewImage] = useState<string>("");
    const [selectedRectId, setSelectedRectId] = useState<string | null>(null);
    const [selectedRectIds, setSelectedRectIds] = useState<Set<string>>(new Set());
    const [focusedRectId, setFocusedRectId] = useState<string | null>(null);
    const [dragMode, setDragMode] = useState<"move" | "resize-corner" | "resize-side" | "new" | null>(null);
    const [dragSide, setDragSide] = useState<"top" | "bottom" | "left" | "right" | null>(null);
    const [customCols, setCustomCols] = useState<string>("3");
    const [customRows, setCustomRows] = useState<string>("3");
    const [presets, setPresets] = useState<any[]>([]);
    const [presetsLoading, setPresetsLoading] = useState(false);
    const [presetName, setPresetName] = useState<string>("");
    const [editingPresetId, setEditingPresetId] = useState<string | null>(null);
    const [editingPresetName, setEditingPresetName] = useState<string>("");
    const [presetsExpanded, setPresetsExpanded] = useState(false);
    const [previewY, setPreviewY] = useState(0);
    const [previewWidth, setPreviewWidth] = useState(0);
    const [previewHeight, setPreviewHeight] = useState(0);
    const [aspectRatioLocked, setAspectRatioLocked] = useState(false);
    const [rectangleColor, setRectangleColor] = useState<string>("#ff1744");
    const aspectRatio = 268 / 288; // 268:288 arány (≈ 0.93:1)
    const [customDistribution, setCustomDistribution] = useState<number[]>([0, 0, 0, 0, 0, 0]);

    // Slice replace mode
    const [replaceMode, setReplaceMode] = useState(false);
    const [selectedSliceForReplace, setSelectedSliceForReplace] = useState<any>(null);
    const [, setOriginalImageSrc] = useState<string>("");

    useEffect(() => {
        if (!libraryId) return;

        fetchImages();
        // Hide sidebar when opening GroupDicing
        if (isSidebarOpen) {
            toggleSidebar();
        }
        return () => {
            // Show sidebar when closing GroupDicing
            if (!isSidebarOpen) {
                toggleSidebar();
            }
        };
    }, [libraryId]);

    // Fetch slices and presets when image changes
    useEffect(() => {
        if (images.length === 0) return;
        fetchSlices();
        fetchPresets();
    }, [currentImageIndex, images]);

    // Handle Delete key to remove selected rectangle(s)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.key === "Delete" || e.key === "Backspace") && selectedRectIds.size > 0) {
                // Delete all selected rectangles
                const newRectangles = rectangles.filter(r => !selectedRectIds.has(r.id));
                setRectangles(newRectangles);
                setSelectedRectIds(new Set());
                setSelectedRectId(null);
                setFocusedRectId(null);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectedRectIds, rectangles]);

    // Follow selected rectangle with preview panel (vertical only)
    useEffect(() => {
        if (!selectedArea || !canvasRef.current) {
            setPreviewY(0);
            return;
        }

        const canvasHeight = canvasRef.current.height;
        const previewPanelHeight = 384; // w-96 h-96 = 384px (Tailwind rem * 16)
        const selectedTop = selectedArea.y;

        // Clamp preview Y position so it stays within canvas bounds
        const clampedY = Math.max(0, Math.min(selectedTop, Math.max(0, canvasHeight - previewPanelHeight)));
        setPreviewY(clampedY);
    }, [selectedArea]);

    // Handle global mouse move and up to allow dragging outside canvas
    useEffect(() => {
        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (!isDrawing || !startPoint || !canvasRef.current) return;

            const rect = canvasRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const dx = x - startPoint.x;
            const dy = y - startPoint.y;

            if (dragMode === "new") {
                // Drawing new rectangle
                const width = Math.abs(dx);
                const height = Math.abs(dy);
                let newX = dx > 0 ? startPoint.x : x;
                let newY = dy > 0 ? startPoint.y : y;

                // Clamp to canvas bounds
                if (canvasRef.current) {
                    if (newX < 0) newX = 0;
                    if (newY < 0) newY = 0;
                    if (newX + width > canvasRef.current.width) newX = canvasRef.current.width - width;
                    if (newY + height > canvasRef.current.height) newY = canvasRef.current.height - height;
                }

                setSelectedArea({ id: "preview", x: newX, y: newY, width, height });

                // Generate preview image
                generatePreviewImage(newX, newY, width, height);
            } else if (dragMode === "move" && selectedRectIds.size > 0) {
                // Handle multi-select move or single-select move
                if (!canvasRef.current) return;

                // Update all selected rectangles with the same offset
                const updatedRectangles = rectangles.map(rect => {
                    if (!selectedRectIds.has(rect.id)) return rect;

                    let newRect = { ...rect };
                    newRect.x = rect.x + dx;
                    newRect.y = rect.y + dy;

                    // Clamp to canvas bounds
                    newRect = clampRectangle(newRect, canvasRef.current!.width, canvasRef.current!.height);
                    return newRect;
                });

                setRectangles(updatedRectangles);

                // Update preview area based on the first selected rectangle
                if (selectedRectId) {
                    const targetRect = updatedRectangles.find(r => r.id === selectedRectId);
                    if (targetRect) {
                        setSelectedArea({ id: "preview", x: targetRect.x, y: targetRect.y, width: targetRect.width, height: targetRect.height });

                        // Generate preview image while moving
                        generatePreviewImage(targetRect.x, targetRect.y, targetRect.width, targetRect.height);
                    }
                }
            } else if (selectedRectId) {
                const targetRect = rectangles.find(r => r.id === selectedRectId);
                if (!targetRect || !canvasRef.current) return;

                let newRect = { ...targetRect };

                if (dragMode === "move") {
                    newRect.x = targetRect.x + dx;
                    newRect.y = targetRect.y + dy;

                    // Clamp to canvas bounds
                    newRect = clampRectangle(newRect, canvasRef.current.width, canvasRef.current.height);

                    setSelectedArea({ id: "preview", x: newRect.x, y: newRect.y, width: newRect.width, height: newRect.height });

                    // Generate preview image while moving
                    generatePreviewImage(newRect.x, newRect.y, newRect.width, newRect.height);
                } else if (dragMode === "resize-corner") {
                    // Determine which corner based on initial position
                    const isLeftSide = x < targetRect.x + targetRect.width / 2;
                    const isTopSide = y < targetRect.y + targetRect.height / 2;

                    if (aspectRatioLocked) {
                        // Use the larger dimension change to maintain aspect ratio
                        const absDx = Math.abs(dx);
                        const absDy = Math.abs(dy);

                        if (absDx > absDy) {
                            // Width changed more, adjust height based on new width
                            if (isLeftSide) {
                                newRect.x = targetRect.x + dx;
                                newRect.width = targetRect.width - dx;
                            } else {
                                newRect.width = targetRect.width + dx;
                            }
                            newRect.height = newRect.width / aspectRatio;

                            if (isTopSide) {
                                newRect.y = targetRect.y - (newRect.height - targetRect.height);
                            }
                        } else {
                            // Height changed more, adjust width based on new height
                            if (isTopSide) {
                                newRect.y = targetRect.y + dy;
                                newRect.height = targetRect.height - dy;
                            } else {
                                newRect.height = targetRect.height + dy;
                            }
                            newRect.width = newRect.height * aspectRatio;

                            if (isLeftSide) {
                                newRect.x = targetRect.x - (newRect.width - targetRect.width);
                            }
                        }
                    } else {
                        if (isLeftSide) {
                            newRect.x = targetRect.x + dx;
                            newRect.width = targetRect.width - dx;
                        } else {
                            newRect.width = targetRect.width + dx;
                        }

                        if (isTopSide) {
                            newRect.y = targetRect.y + dy;
                            newRect.height = targetRect.height - dy;
                        } else {
                            newRect.height = targetRect.height + dy;
                        }
                    }
                } else if (dragMode === "resize-side") {
                    if (aspectRatioLocked) {
                        if (dragSide === "top") {
                            newRect.y = targetRect.y + dy;
                            newRect.height = targetRect.height - dy;
                            newRect.width = newRect.height * aspectRatio;
                            newRect.x = targetRect.x - (newRect.width - targetRect.width) / 2;
                        } else if (dragSide === "bottom") {
                            newRect.height = targetRect.height + dy;
                            newRect.width = newRect.height * aspectRatio;
                            newRect.x = targetRect.x - (newRect.width - targetRect.width) / 2;
                        } else if (dragSide === "left") {
                            newRect.x = targetRect.x + dx;
                            newRect.width = targetRect.width - dx;
                            newRect.height = newRect.width / aspectRatio;
                            newRect.y = targetRect.y - (newRect.height - targetRect.height) / 2;
                        } else if (dragSide === "right") {
                            newRect.width = targetRect.width + dx;
                            newRect.height = newRect.width / aspectRatio;
                            newRect.y = targetRect.y - (newRect.height - targetRect.height) / 2;
                        }
                    } else {
                        if (dragSide === "top") {
                            newRect.y = targetRect.y + dy;
                            newRect.height = targetRect.height - dy;
                        } else if (dragSide === "bottom") {
                            newRect.height = targetRect.height + dy;
                        } else if (dragSide === "left") {
                            newRect.x = targetRect.x + dx;
                            newRect.width = targetRect.width - dx;
                        } else if (dragSide === "right") {
                            newRect.width = targetRect.width + dx;
                        }
                    }
                }

                // Ensure minimum size
                if (newRect.width < 10) newRect.width = 10;
                if (newRect.height < 10) newRect.height = 10;

                // Clamp to canvas bounds
                if (canvasRef.current) {
                    newRect = clampRectangle(newRect, canvasRef.current.width, canvasRef.current.height);
                }

                // Update preview for resize operations
                if (dragMode === "resize-corner" || dragMode === "resize-side") {
                    setSelectedArea({ id: "preview", x: newRect.x, y: newRect.y, width: newRect.width, height: newRect.height });

                    // Generate preview image while resizing
                    generatePreviewImage(newRect.x, newRect.y, newRect.width, newRect.height);
                }

                const updatedRectangles = rectangles.map(r => r.id === selectedRectId ? newRect : r);
                setRectangles(updatedRectangles);

                setStartPoint({ x, y });
            }
        };

        const handleGlobalMouseUp = () => {
            // Finalize new rectangle if dragging outside canvas
            if (dragMode === "new" && selectedArea && selectedArea.width > 10 && selectedArea.height > 10) {
                const newRect: Rectangle = {
                    id: Date.now().toString(),
                    x: selectedArea.x,
                    y: selectedArea.y,
                    width: selectedArea.width,
                    height: selectedArea.height,
                };
                setRectangles(prevRectangles => [...prevRectangles, newRect]);
                setSelectedRectId(newRect.id);
            }

            setIsDrawing(false);
            setStartPoint(null);
            setDragMode(null);
            setDragSide(null);
        };

        if (isDrawing) {
            window.addEventListener("mousemove", handleGlobalMouseMove);
            window.addEventListener("mouseup", handleGlobalMouseUp);

            return () => {
                window.removeEventListener("mousemove", handleGlobalMouseMove);
                window.removeEventListener("mouseup", handleGlobalMouseUp);
            };
        }
    }, [isDrawing, startPoint, dragMode, dragSide, selectedRectId, rectangles, scale]);

    // Draw rectangles on canvas when image loads or rectangles change
    useEffect(() => {
        if (!canvasRef.current || !imgRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw all rectangles
        rectangles.forEach((rect) => {
            const isSelected = selectedRectIds.has(rect.id);
            const isFocused = focusedRectId === rect.id;

            // If there's a focused rectangle, only show it (or show all if none focused)
            if (focusedRectId && !isFocused) {
                // Skip drawing non-focused rectangles (completely hidden)
                return;
            }

            // Draw semi-transparent fill
            ctx.fillStyle = isSelected ? `${rectangleColor}33` : `${rectangleColor}1a`;
            ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

            // Draw border - using dynamic color
            ctx.strokeStyle = isSelected ? rectangleColor : `${rectangleColor}99`;
            ctx.lineWidth = isSelected ? 4 : 3;
            ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);

            // Draw corner handles
            const handleSize = 10;
            ctx.fillStyle = isSelected ? rectangleColor : `${rectangleColor}99`;

            // Top-left
            ctx.fillRect(rect.x - handleSize / 2, rect.y - handleSize / 2, handleSize, handleSize);
            // Top-right
            ctx.fillRect(rect.x + rect.width - handleSize / 2, rect.y - handleSize / 2, handleSize, handleSize);
            // Bottom-left
            ctx.fillRect(rect.x - handleSize / 2, rect.y + rect.height - handleSize / 2, handleSize, handleSize);
            // Bottom-right
            ctx.fillRect(
                rect.x + rect.width - handleSize / 2,
                rect.y + rect.height - handleSize / 2,
                handleSize,
                handleSize
            );

            // Draw side handles (midpoints)
            if (isSelected) {
                const midHandleSize = 8;
                ctx.fillStyle = "#ffeb3b";

                // Top mid
                ctx.fillRect(rect.x + rect.width / 2 - midHandleSize / 2, rect.y - midHandleSize / 2, midHandleSize, midHandleSize);
                // Bottom mid
                ctx.fillRect(rect.x + rect.width / 2 - midHandleSize / 2, rect.y + rect.height - midHandleSize / 2, midHandleSize, midHandleSize);
                // Left mid
                ctx.fillRect(rect.x - midHandleSize / 2, rect.y + rect.height / 2 - midHandleSize / 2, midHandleSize, midHandleSize);
                // Right mid
                ctx.fillRect(rect.x + rect.width - midHandleSize / 2, rect.y + rect.height / 2 - midHandleSize / 2, midHandleSize, midHandleSize);

                // Draw delete button in bottom-right corner
                const buttonSize = 20;
                const buttonPadding = 8;
                const buttonX = rect.x + rect.width - buttonSize - buttonPadding;
                const buttonY = rect.y + rect.height - buttonSize - buttonPadding;

                ctx.fillStyle = "#ff1744";
                ctx.fillRect(buttonX, buttonY, buttonSize, buttonSize);
                ctx.strokeStyle = "#ffffff";
                ctx.lineWidth = 2;
                ctx.strokeRect(buttonX, buttonY, buttonSize, buttonSize);

                // Draw X
                ctx.strokeStyle = "#ffffff";
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(buttonX + 4, buttonY + 4);
                ctx.lineTo(buttonX + buttonSize - 4, buttonY + buttonSize - 4);
                ctx.moveTo(buttonX + buttonSize - 4, buttonY + 4);
                ctx.lineTo(buttonX + 4, buttonY + buttonSize - 4);
                ctx.stroke();
            }
        });

        // Draw preview rectangle while dragging
        if (selectedArea && (dragMode === "new" || dragMode === "move" || dragMode === "resize-corner" || dragMode === "resize-side")) {
            ctx.fillStyle = `${rectangleColor}26`;
            ctx.fillRect(selectedArea.x, selectedArea.y, selectedArea.width, selectedArea.height);
            ctx.strokeStyle = rectangleColor;
            ctx.lineWidth = 3;
            ctx.setLineDash([4, 4]);
            ctx.strokeRect(selectedArea.x, selectedArea.y, selectedArea.width, selectedArea.height);
            ctx.setLineDash([]);
        }
    }, [rectangles, selectedRectId, selectedArea, dragMode, focusedRectId, rectangleColor]);

    const fetchImages = async () => {
        if (!libraryId) return;
        setLoading(true);
        try {
            const response = await fetch(`/api/admin/libraries/${libraryId}/images`);
            if (response.ok) {
                const data = await response.json();
                const imagesList = (data.images || []).reverse();
                setImages(imagesList);
                if (imagesList.length > 0) {
                    setCurrentImageIndex(0);
                } else {
                    showError("Nincsenek képek ebben a könyvtárban.");
                }
            } else {
                showError("Nem sikerült a képek betöltése.");
            }
        } catch (error) {
            console.error("Hiba a képek betöltésekor:", error);
            showError("Hiba a képek betöltésekor.");
        } finally {
            setLoading(false);
        }
    };

    const fetchSlices = async () => {
        const currentImage = images[currentImageIndex];
        if (!currentImage) return;

        setSlicesLoading(true);
        try {
            const response = await fetch(`/api/admin/images/${currentImage.id}/slices`);
            if (response.ok) {
                const data = await response.json();
                setSlices(data.slices || []);
            } else {
                setSlices([]);
            }
        } catch (error) {
            console.error("Hiba a sliceok betöltésekor:", error);
            setSlices([]);
        } finally {
            setSlicesLoading(false);
        }
    };

    // Delete a slice
    const handleDeleteSlice = async (sliceId: number) => {
        try {
            const response = await fetch(`/api/admin/slices/${sliceId}`, {
                method: "DELETE",
            });

            if (response.ok) {
                showSuccess("Slice törölve!");
                await fetchSlices();
            } else {
                showError("Hiba a slice törlése során!");
            }
        } catch (error) {
            console.error("Hiba:", error);
            showError("Hiba a slice törlése során!");
        }
    };

    // Fetch global presets for current user (available for all images)
    const fetchPresets = async () => {
        setPresetsLoading(true);
        try {
            const response = await fetch(`/api/admin/presets/global_index`);
            if (response.ok) {
                const data = await response.json();
                setPresets(data.presets || []);
            } else {
                setPresets([]);
            }
        } catch (error) {
            console.error("Hiba a presetek betöltésekor:", error);
            setPresets([]);
        } finally {
            setPresetsLoading(false);
        }
    };

    // Save current selection as preset
    const handleSavePreset = async () => {
        if (!presetName.trim() || rectangles.length === 0) {
            showError("Adjon meg nevet és jelöljön ki legalább egy területet!");
            return;
        }

        const currentImage = images[currentImageIndex];
        if (!currentImage) return;

        try {
            const response = await fetch(`/api/admin/images/${currentImage.id}/presets`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ preset: { name: presetName, rectangles } }),
            });

            if (response.ok) {
                showSuccess("Preset elmentve!");
                setPresetName("");
                await fetchPresets();
            } else {
                showError("Hiba a preset mentésekor!");
            }
        } catch (error) {
            console.error("Hiba:", error);
            showError("Hiba a preset mentésekor!");
        }
    };

    // Create preset from existing slices
    const handleSavePresetFromSlices = async () => {
        if (!presetName.trim() || slices.length === 0) {
            showError("Adjon meg nevet és kell hogy legyen legalább egy slice!");
            return;
        }

        const currentImage = images[currentImageIndex];
        if (!currentImage) return;

        // Convert slices to rectangles using their position data
        const sliceRectangles: Rectangle[] = slices.map((slice) => ({
            id: `slice-${slice.id}`,
            x: slice.position_x * scale,
            y: slice.position_y * scale,
            width: slice.width * scale,
            height: slice.height * scale,
        }));

        try {
            const response = await fetch(`/api/admin/images/${currentImage.id}/presets`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ preset: { name: presetName, rectangles: sliceRectangles } }),
            });

            if (response.ok) {
                showSuccess("Preset elmentve slice-okból!");
                setPresetName("");
                await fetchPresets();
            } else {
                showError("Hiba a preset mentésekor!");
            }
        } catch (error) {
            console.error("Hiba:", error);
            showError("Hiba a preset mentésekor!");
        }
    };

    // Load preset
    const handleLoadPreset = (preset: any) => {
        setRectangles(preset.rectangles || []);
        setSelectedRectId(null);
        showSuccess("Preset betöltve!");
    };

    // Update preset name
    const handleUpdatePresetName = async (presetId: string) => {
        if (!editingPresetName.trim()) {
            showError("Adjon meg egy nevet!");
            return;
        }

        try {
            const response = await fetch(`/api/admin/presets/${presetId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ preset: { name: editingPresetName } }),
            });

            if (response.ok) {
                showSuccess("Preset név frissítve!");
                setEditingPresetId(null);
                setEditingPresetName("");
                await fetchPresets();
            } else {
                showError("Hiba a preset frissítésekor!");
            }
        } catch (error) {
            console.error("Hiba:", error);
            showError("Hiba a preset frissítésekor!");
        }
    };

    // Delete preset
    const handleDeletePreset = async (presetId: string) => {
        try {
            const response = await fetch(`/api/admin/presets/${presetId}`, {
                method: "DELETE",
            });

            if (response.ok) {
                showSuccess("Preset törölve!");
                await fetchPresets();
            } else {
                showError("Hiba a preset törlése során!");
            }
        } catch (error) {
            console.error("Hiba:", error);
            showError("Hiba a preset törlése során!");
        }
    };

    // Enter replace mode for a slice
    const handleEnterReplaceMode = (slice: any) => {
        // Save the current image source
        if (imgRef.current?.src) {
            setOriginalImageSrc(imgRef.current.src);
        }

        // Create rectangle from the selected slice dimensions using current scale
        const rect: Rectangle = {
            id: `replace-${slice.id}`,
            x: slice.position_x * scale,
            y: slice.position_y * scale,
            width: slice.width * scale,
            height: slice.height * scale,
        };

        setReplaceMode(true);
        setSelectedSliceForReplace(slice);
        setRectangles([rect]);
        setSelectedRectId(rect.id);
        setSelectedArea(rect);
    };

    // Cancel replace mode
    const handleCancelReplace = () => {
        setReplaceMode(false);
        setSelectedSliceForReplace(null);
        setRectangles([]);
        setSelectedRectId(null);
        setSelectedArea(null);
    };

    // Submit slice replace
    const handleSubmitSliceReplace = async () => {
        if (!selectedSliceForReplace || rectangles.length === 0) {
            showError("Jelöljön ki egy területet a csere előtt!");
            return;
        }

        const rect = rectangles[0];
        if (!rect) {
            showError("Nincs kijelölt terület!");
            return;
        }

        try {
            const response = await fetch(`/api/admin/slices/${selectedSliceForReplace.id}/replace`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    position_x: Math.round(rect.x / scale),
                    position_y: Math.round(rect.y / scale),
                    width: Math.round(rect.width / scale),
                    height: Math.round(rect.height / scale),
                }),
            });

            if (response.ok) {
                showSuccess("Slice sikeresen lecserélve!");
                await fetchSlices();
                handleCancelReplace();
            } else {
                const error = await response.json();
                showError(error.error || "Hiba a slice csere során!");
            }
        } catch (error) {
            console.error("Hiba:", error);
            showError("Hiba a slice csere során!");
        }
    };


    // Calculate scale when image loads
    const handleImageLoad = () => {
        if (!imgRef.current || !canvasRef.current) return;

        const imgWidth = imgRef.current.naturalWidth;
        const imgHeight = imgRef.current.naturalHeight;
        const containerWidth = 800; // max-w-6xl ≈ 896px - padding

        const newScale = containerWidth / imgWidth;
        setScale(newScale);

        canvasRef.current.width = imgWidth * newScale;
        canvasRef.current.height = imgHeight * newScale;
    };

    // Helper function to clamp rectangle within canvas bounds
    const clampRectangle = (rect: Rectangle, canvasWidth: number, canvasHeight: number): Rectangle => {
        let clamped = { ...rect };

        if (clamped.x < 0) clamped.x = 0;
        if (clamped.y < 0) clamped.y = 0;
        if (clamped.x + clamped.width > canvasWidth) clamped.x = canvasWidth - clamped.width;
        if (clamped.y + clamped.height > canvasHeight) clamped.y = canvasHeight - clamped.height;

        return clamped;
    };

    // Helper function to generate preview image with margin
    const generatePreviewImage = (x: number, y: number, width: number, height: number) => {
        if (!imgRef.current || width <= 0 || height <= 0) return;

        const margin = 5;
        // Preview canvas mérete a kijelölés méretével azonos (1:1 arány)
        const canvas = document.createElement("canvas");
        canvas.width = width + margin * 2;
        canvas.height = height + margin * 2;
        const ctx = canvas.getContext("2d");
        if (ctx) {
            // Fill with background color
            ctx.fillStyle = "#f0f0f0";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Draw the image portion - x/scale és y/scale az eredeti képen való koordináta
            // width/scale és height/scale az eredeti képen való méret
            // de a canvasra width és height méretben rajzoljuk, szóval 1:1 nagyítás
            ctx.drawImage(
                imgRef.current,
                x / scale,
                y / scale,
                width / scale,
                height / scale,
                margin,
                margin,
                width,
                height
            );

            // Draw border around the selection
            ctx.strokeStyle = "#ff1744";
            ctx.lineWidth = 2;
            ctx.strokeRect(margin, margin, width, height);

            setPreviewImage(canvas.toDataURL());

            // Update preview panel dimensions based on selection size
            // Max height 600px, preserve aspect ratio
            const maxHeight = 600;
            let previewHeight = Math.min(height, maxHeight);
            let previewWidth = (previewHeight / height) * width;

            // Max width 600px for preview
            const maxWidth = 600;
            if (previewWidth > maxWidth) {
                previewWidth = maxWidth;
                previewHeight = (previewWidth / width) * height;
            }

            setPreviewWidth(previewWidth);
            setPreviewHeight(previewHeight);
        }
    };

    // Helper function to detect what we're dragging
    const detectDragMode = (x: number, y: number, targetRect: Rectangle): { mode: "move" | "resize-corner" | "resize-side" | null; side?: "top" | "bottom" | "left" | "right" } => {
        const handleSize = 12;
        const midHandleSize = 10;

        // Check corners
        if (Math.abs(x - targetRect.x) < handleSize && Math.abs(y - targetRect.y) < handleSize) {
            return { mode: "resize-corner" };
        }
        if (Math.abs(x - (targetRect.x + targetRect.width)) < handleSize && Math.abs(y - targetRect.y) < handleSize) {
            return { mode: "resize-corner" };
        }
        if (Math.abs(x - targetRect.x) < handleSize && Math.abs(y - (targetRect.y + targetRect.height)) < handleSize) {
            return { mode: "resize-corner" };
        }
        if (Math.abs(x - (targetRect.x + targetRect.width)) < handleSize && Math.abs(y - (targetRect.y + targetRect.height)) < handleSize) {
            return { mode: "resize-corner" };
        }

        // Check sides
        if (Math.abs(y - targetRect.y) < midHandleSize && x > targetRect.x && x < targetRect.x + targetRect.width) {
            return { mode: "resize-side", side: "top" };
        }
        if (Math.abs(y - (targetRect.y + targetRect.height)) < midHandleSize && x > targetRect.x && x < targetRect.x + targetRect.width) {
            return { mode: "resize-side", side: "bottom" };
        }
        if (Math.abs(x - targetRect.x) < midHandleSize && y > targetRect.y && y < targetRect.y + targetRect.height) {
            return { mode: "resize-side", side: "left" };
        }
        if (Math.abs(x - (targetRect.x + targetRect.width)) < midHandleSize && y > targetRect.y && y < targetRect.y + targetRect.height) {
            return { mode: "resize-side", side: "right" };
        }

        // Check if inside rectangle
        if (x > targetRect.x && x < targetRect.x + targetRect.width && y > targetRect.y && y < targetRect.y + targetRect.height) {
            return { mode: "move" };
        }

        return { mode: null };
    };

    // Helper function to detect if clicking on delete button
    const isClickingDeleteButton = (x: number, y: number, rect: Rectangle): boolean => {
        const buttonSize = 20;
        const buttonPadding = 8;
        const buttonX = rect.x + rect.width - buttonSize - buttonPadding;
        const buttonY = rect.y + rect.height - buttonSize - buttonPadding;

        return x > buttonX && x < buttonX + buttonSize && y > buttonY && y < buttonY + buttonSize;
    };

    const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const isCtrlPressed = e.ctrlKey || e.metaKey;

        // Check if clicking on any selected rectangle's delete button
        selectedRectIds.forEach(rectId => {
            const selectedRect = rectangles.find(r => r.id === rectId);
            if (selectedRect && isClickingDeleteButton(x, y, selectedRect)) {
                // Delete all selected rectangles
                const newRectangles = rectangles.filter(r => !selectedRectIds.has(r.id));
                setRectangles(newRectangles);
                setSelectedRectIds(new Set());
                setSelectedRectId(null);
                setFocusedRectId(null);
                return;
            }
        });

        // If a rectangle is already selected, check for resize/move
        if (selectedRectIds.size > 0 && selectedRectId) {
            const selectedRect = rectangles.find(r => r.id === selectedRectId);
            if (selectedRect) {
                const detection = detectDragMode(x, y, selectedRect);
                if (detection.mode && detection.mode !== "move") {
                    setDragMode(detection.mode as any);
                    if (detection.side) {
                        setDragSide(detection.side);
                    }
                    setStartPoint({ x, y });
                    setIsDrawing(true);
                    return;
                } else if (detection.mode === "move") {
                    // Multi-select move: if hovering over selected rect in move mode, start dragging all selected
                    setDragMode("move");
                    setStartPoint({ x, y });
                    setIsDrawing(true);
                    return;
                }
            }
        }

        // Check if clicking on an existing rectangle
        for (let i = rectangles.length - 1; i >= 0; i--) {
            const detection = detectDragMode(x, y, rectangles[i]);
            if (detection.mode) {
                const clickedId = rectangles[i].id;

                if (isCtrlPressed) {
                    // Multi-select mode: toggle selection, but only drag if clicking on move area
                    const newSelected = new Set(selectedRectIds);
                    if (newSelected.has(clickedId)) {
                        newSelected.delete(clickedId);
                    } else {
                        newSelected.add(clickedId);
                    }
                    setSelectedRectIds(newSelected);
                    setSelectedRectId(clickedId);

                    // Start dragging if in move mode
                    if (detection.mode === "move") {
                        setDragMode("move");
                        setStartPoint({ x, y });
                        setIsDrawing(true);
                    }
                } else {
                    // Single select mode
                    // If there's already a focused rectangle and clicking on a hidden one, just clear focus
                    if (focusedRectId && focusedRectId !== clickedId) {
                        setFocusedRectId(null);
                        setSelectedRectIds(new Set());
                        setSelectedRectId(null);
                        return;
                    }
                    // Otherwise activate focus on this rectangle
                    setSelectedRectIds(new Set([clickedId]));
                    setSelectedRectId(clickedId);
                    setFocusedRectId(clickedId);
                    setDragMode(detection.mode as any);
                    if (detection.side) {
                        setDragSide(detection.side);
                    }
                    setStartPoint({ x, y });
                    setIsDrawing(true);
                }
                return;
            }
        }

        // Start new rectangle or deselect
        if (!isCtrlPressed) {
            setSelectedRectIds(new Set());
            setSelectedRectId(null);
            // If there was a focused rectangle, just clear focus without drawing
            if (focusedRectId) {
                setFocusedRectId(null);
                return;
            }
        }
        setDragMode("new");
        setStartPoint({ x, y });
        setIsDrawing(true);
    };

    const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current) {
            return;
        }

        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Update cursor based on hover
        if (selectedRectId) {
            const targetRect = rectangles.find(r => r.id === selectedRectId);
            if (targetRect) {
                const detection = detectDragMode(x, y, targetRect);
                switch (detection.mode) {
                    case "resize-corner":
                        canvasRef.current.style.cursor = selectedRectIds.size > 1 ? "grab" : "nwse-resize";
                        break;
                    case "resize-side":
                        canvasRef.current.style.cursor = selectedRectIds.size > 1 ? "grab" : (detection.side === "top" || detection.side === "bottom" ? "ns-resize" : "ew-resize");
                        break;
                    case "move":
                        canvasRef.current.style.cursor = "grab";
                        break;
                    default:
                        canvasRef.current.style.cursor = "crosshair";
                }
            }
        } else {
            canvasRef.current.style.cursor = "crosshair";
        }

        // If not drawing, don't update
        if (!isDrawing || !startPoint) return;

        const dx = x - startPoint.x;
        const dy = y - startPoint.y;

        if (dragMode === "new") {
            // Drawing new rectangle
            const width = Math.abs(dx);
            const height = Math.abs(dy);
            let newX = dx > 0 ? startPoint.x : x;
            let newY = dy > 0 ? startPoint.y : y;

            // Clamp to canvas bounds
            if (canvasRef.current) {
                if (newX < 0) newX = 0;
                if (newY < 0) newY = 0;
                if (newX + width > canvasRef.current.width) newX = canvasRef.current.width - width;
                if (newY + height > canvasRef.current.height) newY = canvasRef.current.height - height;
            }

            setSelectedArea({ id: "preview", x: newX, y: newY, width, height });

            // Generate preview image
            if (imgRef.current && width > 0 && height > 0) {
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    ctx.drawImage(
                        imgRef.current,
                        newX / scale,
                        newY / scale,
                        width / scale,
                        height / scale,
                        0,
                        0,
                        width,
                        height
                    );
                    setPreviewImage(canvas.toDataURL());
                }
            }
        } else if (selectedRectId) {
            const targetRect = rectangles.find(r => r.id === selectedRectId);
            if (!targetRect || !canvasRef.current) return;

            let newRect = { ...targetRect };

            if (dragMode === "move") {
                newRect.x = targetRect.x + dx;
                newRect.y = targetRect.y + dy;

                // Clamp to canvas bounds
                newRect = clampRectangle(newRect, canvasRef.current.width, canvasRef.current.height);

                setSelectedArea({ id: "preview", x: newRect.x, y: newRect.y, width: newRect.width, height: newRect.height });

                // Generate preview image while moving
                if (imgRef.current) {
                    const canvas = document.createElement("canvas");
                    canvas.width = newRect.width;
                    canvas.height = newRect.height;
                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                        ctx.drawImage(
                            imgRef.current,
                            newRect.x / scale,
                            newRect.y / scale,
                            newRect.width / scale,
                            newRect.height / scale,
                            0,
                            0,
                            newRect.width,
                            newRect.height
                        );
                        setPreviewImage(canvas.toDataURL());
                    }
                }
            } else if (dragMode === "resize-corner") {
                // Determine which corner based on initial position
                const isLeftSide = x < targetRect.x + targetRect.width / 2;
                const isTopSide = y < targetRect.y + targetRect.height / 2;

                if (aspectRatioLocked) {
                    // Use the larger dimension change to maintain aspect ratio
                    const absDx = Math.abs(dx);
                    const absDy = Math.abs(dy);

                    if (absDx > absDy) {
                        // Width changed more, adjust height based on new width
                        if (isLeftSide) {
                            newRect.x = targetRect.x + dx;
                            newRect.width = targetRect.width - dx;
                        } else {
                            newRect.width = targetRect.width + dx;
                        }
                        newRect.height = newRect.width / aspectRatio;

                        if (isTopSide) {
                            newRect.y = targetRect.y - (newRect.height - targetRect.height);
                        }
                    } else {
                        // Height changed more, adjust width based on new height
                        if (isTopSide) {
                            newRect.y = targetRect.y + dy;
                            newRect.height = targetRect.height - dy;
                        } else {
                            newRect.height = targetRect.height + dy;
                        }
                        newRect.width = newRect.height * aspectRatio;

                        if (isLeftSide) {
                            newRect.x = targetRect.x - (newRect.width - targetRect.width);
                        }
                    }
                } else {
                    if (isLeftSide) {
                        newRect.x = targetRect.x + dx;
                        newRect.width = targetRect.width - dx;
                    } else {
                        newRect.width = targetRect.width + dx;
                    }

                    if (isTopSide) {
                        newRect.y = targetRect.y + dy;
                        newRect.height = targetRect.height - dy;
                    } else {
                        newRect.height = targetRect.height + dy;
                    }
                }
            } else if (dragMode === "resize-side") {
                if (aspectRatioLocked) {
                    if (dragSide === "top") {
                        newRect.y = targetRect.y + dy;
                        newRect.height = targetRect.height - dy;
                        newRect.width = newRect.height * aspectRatio;
                        newRect.x = targetRect.x - (newRect.width - targetRect.width) / 2;
                    } else if (dragSide === "bottom") {
                        newRect.height = targetRect.height + dy;
                        newRect.width = newRect.height * aspectRatio;
                        newRect.x = targetRect.x - (newRect.width - targetRect.width) / 2;
                    } else if (dragSide === "left") {
                        newRect.x = targetRect.x + dx;
                        newRect.width = targetRect.width - dx;
                        newRect.height = newRect.width / aspectRatio;
                        newRect.y = targetRect.y - (newRect.height - targetRect.height) / 2;
                    } else if (dragSide === "right") {
                        newRect.width = targetRect.width + dx;
                        newRect.height = newRect.width / aspectRatio;
                        newRect.y = targetRect.y - (newRect.height - targetRect.height) / 2;
                    }
                } else {
                    if (dragSide === "top") {
                        newRect.y = targetRect.y + dy;
                        newRect.height = targetRect.height - dy;
                    } else if (dragSide === "bottom") {
                        newRect.height = targetRect.height + dy;
                    } else if (dragSide === "left") {
                        newRect.x = targetRect.x + dx;
                        newRect.width = targetRect.width - dx;
                    } else if (dragSide === "right") {
                        newRect.width = targetRect.width + dx;
                    }
                }
            }

            // Ensure minimum size
            if (newRect.width < 10) newRect.width = 10;
            if (newRect.height < 10) newRect.height = 10;

            // Clamp to canvas bounds
            if (canvasRef.current) {
                newRect = clampRectangle(newRect, canvasRef.current.width, canvasRef.current.height);
            }

            // Update preview for resize operations
            if (dragMode === "resize-corner" || dragMode === "resize-side") {
                setSelectedArea({ id: "preview", x: newRect.x, y: newRect.y, width: newRect.width, height: newRect.height });

                // Generate preview image while resizing
                if (imgRef.current) {
                    const canvas = document.createElement("canvas");
                    canvas.width = newRect.width;
                    canvas.height = newRect.height;
                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                        ctx.drawImage(
                            imgRef.current,
                            newRect.x / scale,
                            newRect.y / scale,
                            newRect.width / scale,
                            newRect.height / scale,
                            0,
                            0,
                            newRect.width,
                            newRect.height
                        );
                        setPreviewImage(canvas.toDataURL());
                    }
                }
            }

            const updatedRectangles = rectangles.map(r => r.id === selectedRectId ? newRect : r);
            setRectangles(updatedRectangles);

            setStartPoint({ x, y });
        }
    };

    const handleCanvasMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing || !startPoint) return;

        if (dragMode === "new" && startPoint && canvasRef.current) {
            const rect = canvasRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const width = Math.abs(x - startPoint.x);
            const height = Math.abs(y - startPoint.y);
            const newX = Math.min(startPoint.x, x);
            const newY = Math.min(startPoint.y, y);

            if (width > 10 && height > 10) {
                const newRect: Rectangle = {
                    id: Date.now().toString(),
                    x: newX,
                    y: newY,
                    width,
                    height,
                };
                setRectangles(prevRectangles => [...prevRectangles, newRect]);
                setSelectedRectId(newRect.id);
            }
        } else if (selectedRectId && (dragMode === "move" || dragMode === "resize-corner" || dragMode === "resize-side")) {
            // Generate preview image for the current selected rectangle
            const targetRect = rectangles.find(r => r.id === selectedRectId);
            if (targetRect && imgRef.current) {
                const canvas = document.createElement("canvas");
                canvas.width = targetRect.width;
                canvas.height = targetRect.height;
                const ctx = canvas.getContext("2d");
                if (ctx) {
                    ctx.drawImage(
                        imgRef.current,
                        targetRect.x / scale,
                        targetRect.y / scale,
                        targetRect.width / scale,
                        targetRect.height / scale,
                        0,
                        0,
                        targetRect.width,
                        targetRect.height
                    );
                    setPreviewImage(canvas.toDataURL());
                    setSelectedArea({ id: "preview", x: targetRect.x, y: targetRect.y, width: targetRect.width, height: targetRect.height });
                }
            }
        }

        setIsDrawing(false);
        setStartPoint(null);
        setDragMode(null);
        setDragSide(null);
    };

    const handleDice = async () => {
        if (rectangles.length === 0) {
            showError("Jelöljön ki legalább egy területet!");
            return;
        }

        // Prevent double-clicking
        if (diceLoading) {
            return;
        }

        setDiceLoading(true);

        try {
            const currentImage = images[currentImageIndex];
            const response = await fetch(`/api/admin/images/${currentImage.id}/slice`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    rectangles: rectangles.map((r) => ({
                        x: Math.round(r.x / scale),
                        y: Math.round(r.y / scale),
                        width: Math.round(r.width / scale),
                        height: Math.round(r.height / scale),
                    })),
                }),
            });

            if (response.ok) {
                showSuccess("Képek feldarabolva és feltöltve!");
                // Ne töröljük a rectangles-t, csak frissítjük a sliceokat
                await fetchSlices();
            } else {
                showError("Hiba a darabolás során!");
            }
        } catch (error) {
            console.error("Hiba:", error);
            showError("Hiba a darabolás során!");
        } finally {
            setDiceLoading(false);
        }
    };

    const handleBack = () => {
        navigate("/admin/dicing");
    };

    // Grid slicing function
    const createGridRectangles = (cols: number, rows: number) => {
        if (!canvasRef.current) return;

        const canvasWidth = canvasRef.current.width;
        const canvasHeight = canvasRef.current.height;
        const gap = 10; // 10px gap between squares
        const padding = 8; // Margin around all rectangles to keep them inside the image

        // Calculate the available space after accounting for gaps and padding
        const totalGapHorizontal = gap * (cols - 1);
        const totalGapVertical = gap * (rows - 1);

        const rectWidth = (canvasWidth - (padding * 2) - totalGapHorizontal) / cols;
        const rectHeight = (canvasHeight - (padding * 2) - totalGapVertical) / rows;

        const newRectangles: Rectangle[] = [];

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const x = padding + col * (rectWidth + gap);
                const y = padding + row * (rectHeight + gap);

                newRectangles.push({
                    id: `grid-${Date.now()}-${row}-${col}`,
                    x: x,
                    y: y,
                    width: rectWidth,
                    height: rectHeight,
                });
            }
        }

        setRectangles(newRectangles);
        setSelectedRectId(null);
        showSuccess(`${cols}x${rows} grid létrehozva (${cols * rows} négyzet)`);
    };

    // Create grid based on custom distribution
    const createDistributedGrid = () => {
        if (!canvasRef.current) return;

        const totalSquares = customDistribution.reduce((sum, count) => sum + count, 0);
        if (totalSquares === 0) {
            showError("Legalább egy négyzetet adjunk hozzá!");
            return;
        }

        const canvasWidth = canvasRef.current.width;
        const canvasHeight = canvasRef.current.height;
        const gap = 10;
        const padding = 8; // Margin around all rectangles to keep them inside the image

        // Filter to only active rows (with at least 1 square)
        const activeRows = customDistribution
            .map((count, index) => ({ count, index, originalIndex: index }))
            .filter(row => row.count > 0);

        if (activeRows.length === 0) {
            showError("Nincs aktív sor!");
            return;
        }

        // Calculate gaps only for active rows, accounting for padding
        const numActiveRows = activeRows.length;
        const totalGapVertical = gap * (numActiveRows - 1);
        const availableHeight = canvasHeight - (padding * 2) - totalGapVertical;
        const rowHeight = availableHeight / numActiveRows;

        const newRectangles: Rectangle[] = [];
        let timeNow = Date.now();

        activeRows.forEach((row, activeRowIndex) => {
            const colsInRow = row.count;
            const totalGapHorizontal = gap * (colsInRow - 1);
            const rectWidth = (canvasWidth - (padding * 2) - totalGapHorizontal) / colsInRow;
            const y = padding + activeRowIndex * (rowHeight + gap);

            for (let col = 0; col < colsInRow; col++) {
                const x = padding + col * (rectWidth + gap);

                newRectangles.push({
                    id: `dist-${timeNow}-${row.originalIndex}-${col}`,
                    x: x,
                    y: y,
                    width: rectWidth,
                    height: rowHeight,
                });
            }
        });

        setRectangles(newRectangles);
        setSelectedRectId(null);
        showSuccess(`Eloszlás létrehozva (${totalSquares} négyzet)`);
    };

    const handleDistributionChange = (rowIndex: number, change: number) => {
        const newDistribution = [...customDistribution];
        newDistribution[rowIndex] = Math.max(0, newDistribution[rowIndex] + change);
        setCustomDistribution(newDistribution);
    };

    return (
        <div className="min-h-screen w-full bg-background flex flex-col">
            {/* Header */}
            <div className="border-b border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleBack}
                        className="p-2 hover:bg-gray-200 dark:hover:bg-slate-600 rounded transition-colors"
                        aria-label="Vissza"
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold">Csoportos Darabolás</h1>
                        <p className="text-sm text-muted-foreground">{libraryName}</p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-grow flex flex-col overflow-hidden">
                {loading ? (
                    <p className="text-center text-muted-foreground flex-grow flex items-center justify-center">Képek betöltése...</p>
                ) : images.length === 0 ? (
                    <p className="text-center text-muted-foreground flex-grow flex items-center justify-center">Nincsenek képek ebben a könyvtárban.</p>
                ) : (
                    <>
                        {/* Grid Presets Section */}
                        <div className="border-b border-gray-200 dark:border-slate-700 bg-blue-50 dark:bg-slate-800 p-4">
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Előre definiált rácsok</h3>
                            <div className="flex flex-wrap gap-2 mb-4">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => createGridRectangles(3, 3)}
                                    className="bg-white dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-slate-600"
                                >
                                    3x3
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => createGridRectangles(3, 4)}
                                    className="bg-white dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-slate-600"
                                >
                                    3x4
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => createGridRectangles(3, 5)}
                                    className="bg-white dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-slate-600"
                                >
                                    3x5
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => createGridRectangles(4, 3)}
                                    className="bg-white dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-slate-600"
                                >
                                    4x3
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => createGridRectangles(4, 4)}
                                    className="bg-white dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-slate-600"
                                >
                                    4x4
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => createGridRectangles(4, 5)}
                                    className="bg-white dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-slate-600"
                                >
                                    4x5
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => createGridRectangles(5, 2)}
                                    className="bg-white dark:bg-slate-700 hover:bg-blue-100 dark:hover:bg-slate-600"
                                >
                                    5x2
                                </Button>
                            </div>

                            {/* Grid Panels - Side by Side */}
                            <div className="flex gap-3">
                                {/* Custom Grid Input - Left Panel */}
                                <div className="flex-1 bg-white dark:bg-slate-700 p-3 rounded border border-gray-200 dark:border-slate-600">
                                    <div className="flex items-center justify-between mb-2 gap-2">
                                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                            Soronkénti sz. (6 sor):
                                        </label>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            Σ: {customDistribution.reduce((a, b) => a + b, 0)}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-6 gap-1 mb-2">
                                        {customDistribution.map((count, rowIndex) => (
                                            <div key={rowIndex} className="flex flex-col items-center gap-0.5">
                                                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                                    {rowIndex + 1}
                                                </span>
                                                <div className="flex items-center gap-0.5">
                                                    <button
                                                        onClick={() => handleDistributionChange(rowIndex, -1)}
                                                        disabled={count === 0}
                                                        className="w-5 h-5 flex items-center justify-center bg-red-500 hover:bg-red-600 disabled:bg-gray-300 dark:disabled:bg-slate-600 text-white rounded text-xs font-bold transition-colors"
                                                    >
                                                        −
                                                    </button>
                                                    <span className="w-6 text-center font-bold text-gray-900 dark:text-white text-xs">
                                                        {count}
                                                    </span>
                                                    <button
                                                        onClick={() => handleDistributionChange(rowIndex, 1)}
                                                        className="w-5 h-5 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded text-xs font-bold transition-colors"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <Button
                                        onClick={createDistributedGrid}
                                        disabled={customDistribution.reduce((a, b) => a + b, 0) === 0}
                                        size="sm"
                                        className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white disabled:opacity-50"
                                    >
                                        Felvesz
                                    </Button>
                                </div>

                                {/* Regular Custom Grid Input - Right Panel */}
                                <div className="flex-1 flex flex-col gap-2 bg-white dark:bg-slate-700 p-3 rounded border border-gray-200 dark:border-slate-600">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                        Egyéni rács (retro):
                                    </label>
                                    <div className="flex items-center gap-2 flex-1">
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="number"
                                                min="1"
                                                max="20"
                                                value={customCols}
                                                onChange={(e) => setCustomCols(e.target.value)}
                                                className="w-12 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-600 text-sm text-gray-900 dark:text-white"
                                                placeholder="3"
                                            />
                                            <span className="text-gray-500 dark:text-gray-400 text-sm">×</span>
                                            <input
                                                type="number"
                                                min="1"
                                                max="20"
                                                value={customRows}
                                                onChange={(e) => setCustomRows(e.target.value)}
                                                className="w-12 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-600 text-sm text-gray-900 dark:text-white"
                                                placeholder="3"
                                            />
                                        </div>
                                        <Button
                                            size="sm"
                                            onClick={() => {
                                                const cols = parseInt(customCols) || 3;
                                                const rows = parseInt(customRows) || 3;

                                                if (cols < 1 || cols > 20 || rows < 1 || rows > 20) {
                                                    showError("A számoknak 1 és 20 között kell lenniük!");
                                                    return;
                                                }

                                                createGridRectangles(cols, rows);
                                            }}
                                            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white whitespace-nowrap"
                                        >
                                            Felhelyez
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Toolbar: Image Navigation and Action Buttons */}
                        <div className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 p-4 flex items-center justify-between">
                            {/* Left: Image Info and Navigation */}
                            <div className="flex items-center gap-4">
                                <div className="text-center">
                                    <p className="text-sm font-semibold text-muted-foreground">
                                        {currentImageIndex + 1} / {images.length}
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            if (currentImageIndex > 0) setCurrentImageIndex(currentImageIndex - 1);
                                            setRectangles([]);
                                            setSelectedRectId(null);
                                        }}
                                        disabled={currentImageIndex === 0}
                                        variant="outline"
                                    >
                                        <ChevronLeft className="mr-2 h-4 w-4" />
                                        Előző
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            if (currentImageIndex < images.length - 1) setCurrentImageIndex(currentImageIndex + 1);
                                            setRectangles([]);
                                            setSelectedRectId(null);
                                        }}
                                        disabled={currentImageIndex === images.length - 1}
                                        variant="outline"
                                    >
                                        Következő
                                        <ChevronRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            {/* Right: Action Buttons */}
                            <div className="flex gap-2 items-center">
                                {/* Color Picker Control */}
                                <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-700 rounded border border-gray-300 dark:border-slate-600">
                                    <label htmlFor="rectangleColor" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer whitespace-nowrap">
                                        Szín:
                                    </label>
                                    <input
                                        type="color"
                                        id="rectangleColor"
                                        value={rectangleColor}
                                        onChange={(e) => setRectangleColor(e.target.value)}
                                        className="w-8 h-8 cursor-pointer border-0 rounded"
                                        title="Négyzetek színének megváltoztatása"
                                    />
                                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{rectangleColor}</span>
                                </div>

                                {/* Aspect Ratio Lock Control */}
                                <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-700 rounded border border-gray-300 dark:border-slate-600">
                                    <input
                                        type="checkbox"
                                        id="aspectRatioToggle"
                                        checked={aspectRatioLocked}
                                        onChange={(e) => setAspectRatioLocked(e.target.checked)}
                                        className="w-4 h-4 cursor-pointer"
                                    />
                                    <label htmlFor="aspectRatioToggle" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer whitespace-nowrap">
                                        2:3 arány
                                    </label>
                                </div>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setRectangles([])}
                                    disabled={rectangles.length === 0}
                                >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Összes törlése ({rectangles.length})
                                </Button>
                                <Button
                                    variant="default"
                                    onClick={handleDice}
                                    disabled={rectangles.length === 0 || diceLoading}
                                    size="sm"
                                >
                                    <Copy className="mr-2 h-4 w-4" />
                                    Darabolás ({rectangles.length})
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleBack}>
                                    Vissza
                                </Button>
                            </div>
                        </div>

                        {/* Main layout - Preview, Canvas and Slices */}
                        <div className="flex gap-6 flex-grow overflow-hidden p-6">
                            {/* Left: Preview Panel - Dynamic Size Based on Selection */}
                            <div
                                ref={previewRef}
                                className="bg-blue-50 dark:bg-slate-900 p-4 rounded-lg border border-blue-200 dark:border-slate-700 overflow-hidden flex-shrink-0 transition-transform duration-100 ease-out w-96 h-96"
                                style={{
                                    transform: `translateY(${previewY}px)`,
                                }}
                            >
                                <div className="w-full h-full bg-white dark:bg-slate-800 rounded border border-blue-300 dark:border-blue-600 overflow-hidden flex items-center justify-center"
                                    style={{
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                    } as React.CSSProperties & { scrollbarWidth: string; msOverflowStyle: string }}>
                                    <style>{`
                                        .preview-container::-webkit-scrollbar { display: none; }
                                    `}</style>
                                    {previewImage ? (
                                        <img
                                            src={previewImage}
                                            alt="Preview"
                                            className="preview-container block flex-shrink-0"
                                            style={{
                                                width: previewWidth > 0 ? `${previewWidth - 10}px` : 'auto',
                                                height: previewHeight > 0 ? `${previewHeight - 10}px` : 'auto',
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    ) : (
                                        <p className="text-xs text-muted-foreground text-center italic px-2">
                                            Jelöljön ki egy területet
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Middle: Canvas */}
                            <div className="flex-1 flex flex-col">
                                {/* Replace Mode UI Panel - Above Canvas */}
                                {replaceMode && selectedSliceForReplace && (
                                    <div className="mb-4 bg-white dark:bg-slate-800 rounded-lg shadow-xl border-2 border-blue-500 dark:border-blue-400 p-4 transition-all duration-300">
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="font-bold text-blue-600 dark:text-blue-400">
                                                        🔄 Slice #{selectedSliceForReplace.slice_number} csere
                                                    </h3>
                                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                                        Pozíció módosítása az eredeti képen
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={handleCancelReplace}
                                                    className="flex-1 px-3 py-2 bg-gray-200 dark:bg-slate-600 hover:bg-gray-300 dark:hover:bg-slate-500 text-gray-800 dark:text-white text-sm font-medium rounded transition-colors"
                                                >
                                                    Mégse
                                                </button>
                                                <button
                                                    onClick={handleSubmitSliceReplace}
                                                    className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white text-sm font-medium rounded transition-colors"
                                                >
                                                    ✓ Véglegesítés
                                                </button>
                                            </div>

                                            <p className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-slate-700 p-2 rounded">
                                                Módosítsd a négyzet pozícióját és méretét az eredeti képen, majd kattints a Véglegesítésre.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Canvas Wrapper */}
                                <div className={`relative rounded-lg overflow-hidden flex justify-center items-start w-full transition-colors ${slices.length > 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-100 dark:bg-slate-800'}`}>
                                    <img
                                        ref={imgRef}
                                        src={images[currentImageIndex].image_url || ""}
                                        alt="Kép"
                                        className="block max-w-full"
                                        onLoad={handleImageLoad}
                                        style={{ display: "none" }}
                                    />
                                    <canvas
                                        ref={canvasRef}
                                        onMouseDown={handleCanvasMouseDown}
                                        onMouseMove={handleCanvasMouseMove}
                                        onMouseUp={handleCanvasMouseUp}
                                        className="block border-2 border-gray-300 dark:border-slate-600 cursor-crosshair"
                                        style={{
                                            backgroundImage: `url(${images[currentImageIndex].image_url})`,
                                            backgroundSize: "contain",
                                            backgroundRepeat: "no-repeat",
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Right: Slices and Presets */}
                            <div className="w-96 flex flex-col gap-4">
                                {/* Presets Panel */}
                                <div className={`bg-purple-50 dark:bg-slate-900 p-4 rounded-lg border border-purple-200 dark:border-slate-700 flex flex-col transition-all ${presetsExpanded ? 'flex-1' : ''}`}>
                                    <button
                                        onClick={() => setPresetsExpanded(!presetsExpanded)}
                                        className="flex items-center justify-between w-full mb-4"
                                    >
                                        <h3 className="font-semibold text-purple-900 dark:text-purple-300">
                                            Presets
                                        </h3>
                                        <span className="text-xl text-purple-600 dark:text-purple-400">
                                            {presetsExpanded ? '▼' : '▶'}
                                        </span>
                                    </button>

                                    {presetsExpanded && (
                                        <div className="flex flex-col flex-1 overflow-hidden">
                                            {/* Save Preset */}
                                            <div className="mb-4 flex gap-2">
                                                <input
                                                    type="text"
                                                    value={presetName}
                                                    onChange={(e) => setPresetName(e.target.value)}
                                                    placeholder="Preset neve..."
                                                    className="flex-1 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-600 text-sm text-gray-900 dark:text-white"
                                                />
                                                <Button
                                                    size="sm"
                                                    onClick={handleSavePreset}
                                                    disabled={rectangles.length === 0}
                                                    className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-600 text-white"
                                                >
                                                    Mentés
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={handleSavePresetFromSlices}
                                                    disabled={slices.length === 0}
                                                    className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600 text-white"
                                                    title="Meglévő slice-okból presetet készít"
                                                >
                                                    Slice-ből
                                                </Button>
                                            </div>

                                            {/* Presets List */}
                                            {presetsLoading ? (
                                                <p className="text-sm text-muted-foreground text-center py-4">Betöltés...</p>
                                            ) : presets.length === 0 ? (
                                                <p className="text-sm text-muted-foreground italic text-center py-4">Nincsenek mentett presets</p>
                                            ) : (
                                                <div className="flex flex-col gap-2 overflow-y-auto pr-2 flex-1">
                                                    {[...presets].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((preset) => (
                                                        <div
                                                            key={preset.id}
                                                            className="bg-white dark:bg-slate-800 rounded border border-purple-200 dark:border-slate-700 p-2 flex items-center justify-between group hover:shadow-sm transition-shadow"
                                                        >
                                                            {editingPresetId === preset.id ? (
                                                                <div className="flex gap-1 flex-1">
                                                                    <input
                                                                        type="text"
                                                                        value={editingPresetName}
                                                                        onChange={(e) => setEditingPresetName(e.target.value)}
                                                                        className="flex-1 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-600 text-xs text-gray-900 dark:text-white"
                                                                        autoFocus
                                                                    />
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => handleUpdatePresetName(preset.id)}
                                                                        className="bg-blue-500 hover:bg-blue-600 text-white h-6 px-2"
                                                                    >
                                                                        ✓
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => setEditingPresetId(null)}
                                                                        variant="outline"
                                                                        className="h-6 px-2"
                                                                    >
                                                                        ✕
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                                                            {preset.name}
                                                                        </p>
                                                                        <p className="text-xs text-muted-foreground">
                                                                            {preset.rectangles?.length || 0} rect
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleLoadPreset(preset)}
                                                                            className="bg-blue-500 hover:bg-blue-600 text-white h-6 px-2"
                                                                        >
                                                                            📂
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                setEditingPresetId(preset.id);
                                                                                setEditingPresetName(preset.name);
                                                                            }}
                                                                            variant="outline"
                                                                            className="h-6 px-2"
                                                                        >
                                                                            ✎
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleDeletePreset(preset.id)}
                                                                            className="bg-red-500 hover:bg-red-600 text-white h-6 px-2"
                                                                        >
                                                                            🗑
                                                                        </Button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Slices Panel */}
                                <div className="bg-green-50 dark:bg-slate-900 p-4 rounded-lg border border-green-200 dark:border-slate-700 flex-1 flex flex-col">
                                    <h3 className="font-semibold mb-4 text-green-900 dark:text-green-300">
                                        Meglévő Slice-ok ({slices.length})
                                    </h3>
                                    {slicesLoading ? (
                                        <p className="text-sm text-muted-foreground text-center py-8">Betöltés...</p>
                                    ) : slices.length === 0 ? (
                                        <p className="text-sm text-muted-foreground italic text-center py-8">Nincs slice az adott képhez</p>
                                    ) : (
                                        <div className="flex flex-col gap-3 overflow-y-auto pr-2 flex-1">
                                            {slices.map((slice) => (
                                                <div
                                                    key={slice.id}
                                                    className="bg-white dark:bg-slate-800 rounded border border-green-200 dark:border-slate-700 hover:shadow-md transition-shadow overflow-hidden flex flex-col"
                                                >
                                                    {/* Slice Image - Full Display */}
                                                    {slice.slice_url ? (
                                                        <div className="w-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center p-2 min-h-32">
                                                            <img
                                                                src={slice.slice_url}
                                                                alt={`Slice ${slice.slice_number}`}
                                                                className="max-w-full max-h-full object-contain"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="w-full h-32 bg-gray-200 dark:bg-slate-700 flex items-center justify-center text-xs text-muted-foreground">
                                                            Nincs előnézet
                                                        </div>
                                                    )}
                                                    {/* Buttons Row */}
                                                    <div className="flex gap-2 p-2 bg-gray-50 dark:bg-slate-700">
                                                        {/* Replace Button - Always available */}
                                                        <button
                                                            onClick={() => handleEnterReplaceMode(slice)}
                                                            className="flex-1 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white text-xs font-medium py-2 transition-colors rounded flex items-center justify-center gap-1"
                                                            title="Slice képét lecserélni"
                                                        >
                                                            🔄 Csere
                                                        </button>
                                                        {/* Delete Button - Only show if no product mapping */}
                                                        {!slice.has_product_mapping && (
                                                            <button
                                                                onClick={() => handleDeleteSlice(slice.id)}
                                                                className="flex-1 bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white text-xs font-medium py-2 transition-colors rounded flex items-center justify-center gap-1"
                                                            >
                                                                <Trash2 size={14} />
                                                                Törlés
                                                            </button>
                                                        )}
                                                    </div>
                                                    {slice.has_product_mapping && (
                                                        <div className="w-full bg-gray-300 dark:bg-slate-600 text-gray-700 dark:text-gray-300 text-xs font-medium py-1 text-center">
                                                            Termékhez rendelve
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div >
        </div >
    );
};

export default GroupDicingPage;
